import type {
  Account,
  AccountNode,
  Entry,
  EntryKind,
  EntrySource,
  Posting,
  PostingInput,
  ReplaceEntryInput,
} from '@taler/api-client'
import {
  type CurrencyCode,
  CurrencyMismatchError,
  type Instant,
  type LocalDate,
  type Locale,
  type Money,
  requireCurrencyCode,
} from '@taler/domain'
import {
  accountLabel,
  type AccountIndex,
  type AccountLabel,
  categorySign,
  convertedFigure,
  indexAccounts,
  isResidualAccount,
  isRevaluation,
  representedPostingOf,
  revaluationTotalOf,
  valuationFigureOf,
} from './transaction-row.ts'

/** Every posting on the entry, including one the user never typed: a residual leg the server
 * added, recognised by `isResidualAccount`. */
export interface PostingDetail {
  readonly id: string
  readonly accountId: string
  readonly account: AccountLabel
  readonly isResidual: boolean
  readonly accountClass: Account['accountClass'] | undefined
  readonly amount: Money
  /** The amount in the display currency, present only when it is a conversion of `amount`. */
  readonly displayAmount: Money | undefined
  /** A valuation's own change in the display currency; absent on an ordinary entry. */
  readonly valueChange: Money | undefined
  readonly memo: string | undefined
}

export function toPostingDetail(
  posting: Posting,
  accounts: AccountIndex,
  locale: Locale,
  valueChange: Money | undefined,
): PostingDetail {
  const account = accounts.get(posting.accountId)
  return {
    id: posting.id,
    accountId: posting.accountId,
    account:
      account === undefined
        ? { leaf: posting.accountId, path: posting.accountId }
        : accountLabel(account, accounts, locale),
    accountClass: account?.accountClass,
    isResidual: isResidualAccount(account),
    amount: posting.amount,
    displayAmount: convertedFigure(posting.amount, posting.displayAmount),
    valueChange,
    memo: posting.memo,
  }
}

export function toPostingDetails(
  entry: Entry,
  accounts: AccountIndex,
  locale: Locale,
): readonly PostingDetail[] {
  return entry.postings.map((posting) =>
    toPostingDetail(
      posting,
      accounts,
      locale,
      isRevaluation(entry) ? valuationFigureOf(entry, posting) : undefined,
    ),
  )
}

/** What the entry detail dialog shows in view mode - every field the entry form can change, plus
 * every posting the entry carries, so a component built on this never reaches into the API
 * client's own `Entry` type directly. */
export interface EntryDetailView {
  readonly id: string
  readonly date: LocalDate
  readonly title: string
  readonly payee: string | undefined
  readonly memo: string
  readonly notes: string | undefined
  readonly tags: readonly string[]
  readonly kind: EntryKind | undefined
  readonly source: EntrySource
  readonly displayTotal: Money | undefined
  /** Which way the money moved, from the category legs; says the direction of `displayTotal`. */
  readonly categorySign: -1 | 0 | 1
  /** The currency a new posting row starts in: the represented posting's own, else the entry total's. */
  readonly ownCurrency: CurrencyCode
  readonly displayRateMissing: boolean
  readonly postings: readonly PostingDetail[]
  readonly createdAt: Instant
  readonly updatedAt: Instant
  readonly isReversal: boolean
  readonly isRevaluation: boolean
}

export function toEntryDetailView(
  entry: Entry,
  accounts: AccountIndex,
  locale: Locale,
  displayCurrency?: CurrencyCode,
): EntryDetailView {
  const represented = representedPostingOf(entry, accounts, displayCurrency)
  const revaluation = isRevaluation(entry)
  return {
    id: entry.id,
    date: entry.date,
    title: entry.payee ?? entry.memo,
    payee: entry.payee,
    memo: entry.memo,
    notes: entry.notes,
    tags: entry.tags,
    kind: entry.kind,
    source: entry.source,
    displayTotal: revaluation
      ? revaluationTotalOf(entry, accounts)
      : (entry.displayTotal ?? represented?.amount),
    categorySign: categorySign(entry, accounts),
    ownCurrency: (represented?.amount ?? entry.total).currency,
    displayRateMissing: entry.displayRateMissing,
    postings: toPostingDetails(entry, accounts, locale),
    createdAt: entry.createdAt,
    updatedAt: entry.updatedAt,
    isReversal: entry.reversalOfEntryId !== undefined,
    isRevaluation: revaluation,
  }
}

export interface PostableAccountOption {
  readonly id: string
  readonly label: string
  readonly currency: string | undefined
}

/** Postable accounts only (`isPostable`; an asset or liability group never carries a posting) and never an
 * `EQUITY` account: every real `EQUITY` leaf is plumbing (opening, reconciliation, the FX
 * gain/loss residual), never a category a person picks. Not `!isSystem` - the backend marks
 * every catalogue account system, so that would also hide real categories like the car
 * subtree and the prepaid holding account. */
export function postableAccountOptions(
  nodes: readonly AccountNode[],
  locale: Locale,
): PostableAccountOption[] {
  const index = indexAccounts(nodes)
  const out: PostableAccountOption[] = []
  const walk = (level: readonly AccountNode[]): void => {
    for (const node of level) {
      const account: Account = node.account
      if (account.isPostable && account.accountClass !== 'EQUITY' && account.archivedAt === undefined) {
        out.push({
          id: account.id,
          label: accountLabel(account, index, locale).path,
          currency: account.currency,
        })
      }
      walk(node.children)
    }
  }
  walk(nodes)
  return out
}

/** Raw form state for one posting line. `amount` is `null` until a value exists (AmountInput's
 * own empty state) - the redundant `currency` text field is what lets the field size and label
 * itself before an amount is typed, and is checked against `amount`'s own currency at submit,
 * since the two are edited independently and can drift apart. */
export interface PostingDraft {
  readonly accountId: string
  readonly amount: Money | null
  readonly currency: string
  readonly memo?: string
}

/** The exact amount, not rounded to the currency's display digits - opening the edit form
 * never changes what a figure reads until the user actually touches it. */
export function toPostingDraft(posting: PostingDetail): PostingDraft {
  return {
    accountId: posting.accountId,
    amount: posting.amount,
    currency: posting.amount.currency,
    ...(posting.memo === undefined ? {} : { memo: posting.memo }),
  }
}

export interface EntryDraft {
  readonly date: LocalDate
  readonly payee?: string
  readonly memo: string
  readonly notes?: string
  readonly tags: readonly string[]
  readonly postings: readonly PostingDraft[]
}

/** Requires every posting to already carry an amount, so a malformed or incomplete entry
 * never reaches the wire - the throw becomes the mutation's own error, same as the `money()`
 * parse failure this replaces. A posting whose typed currency has drifted from its own
 * amount's currency (the two are edited independently) throws the same way. */
export function toReplaceEntryInput(draft: EntryDraft): ReplaceEntryInput {
  return {
    date: draft.date,
    memo: draft.memo,
    postings: draft.postings.map((posting): PostingInput => {
      if (posting.amount === null) throw new Error('Posting amount is required')
      const currency = requireCurrencyCode(posting.currency)
      if (posting.amount.currency !== currency) {
        throw new CurrencyMismatchError(posting.amount.currency, currency)
      }
      return {
        accountId: posting.accountId,
        amount: posting.amount,
        ...(posting.memo === undefined || posting.memo === '' ? {} : { memo: posting.memo }),
      }
    }),
    tags: draft.tags,
    ...(draft.payee === undefined || draft.payee === '' ? {} : { payee: draft.payee }),
    ...(draft.notes === undefined || draft.notes === '' ? {} : { notes: draft.notes }),
  }
}
